import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { clubsBookingApi } from '@/api/clubs-booking';
import { auth } from '@/store/authStore';
import { getClubSiteSlugFromHostname, getClubSiteUrl } from '@/utils/publicSite';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referralDisplayName, setReferralDisplayName] = useState('');
  const [referralLookupLoading, setReferralLookupLoading] = useState(false);

  useEffect(() => {
    const user = auth.getUser();
    if (!user.isAuthenticated) return;
    navigate(user.role === 'parent' ? '/my-bookings' : '/dashboard', { replace: true });
  }, [navigate]);

  useEffect(() => {
    const modeParam = searchParams.get('mode');
    const referralParam = searchParams.get('ref');
    setReferralCode(referralParam ? referralParam.trim().toUpperCase() : '');
    if (modeParam === 'signup' || modeParam === 'register') {
      setMode('signup');
      return;
    }
    if (modeParam === 'login') {
      setMode('login');
    }
  }, [searchParams]);

  useEffect(() => {
    let active = true;
    const normalizedCode = referralCode.trim().toUpperCase();

    if (mode !== 'signup' || !normalizedCode) {
      setReferralDisplayName('');
      setReferralLookupLoading(false);
      return () => {
        active = false;
      };
    }

    setReferralLookupLoading(true);
    clubsBookingApi
      .getPublicReferralProfile(normalizedCode)
      .then((profile) => {
        if (!active) return;
        const displayName = String(
          profile?.displayName || profile?.clubName || profile?.referrerName || '',
        ).trim();
        setReferralDisplayName(profile?.found ? displayName : '');
      })
      .catch(() => {
        if (!active) return;
        setReferralDisplayName('');
      })
      .finally(() => {
        if (!active) return;
        setReferralLookupLoading(false);
      });

    return () => {
      active = false;
    };
  }, [mode, referralCode]);

  const title = useMemo(
    () => (mode === 'login' ? 'Session Monkey Login' : 'Create your Session Monkey account'),
    [mode],
  );

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response =
        mode === 'login'
          ? await clubsBookingApi.login(email, password)
          : await clubsBookingApi.signup({
              email,
              name,
              companyName,
              password,
              confirmPassword,
              referralCode: referralCode.trim() || undefined,
            });

      if (response.role === 'parent') {
        auth.clearAuth();
        clubsBookingApi.clearAuthToken();
        setError('Parent accounts must sign in via /parent/login');
        return;
      }

      auth.setAuth(response.accessToken, response.id, response.name, email, response.role);
      clubsBookingApi.setAuthToken(response.accessToken);

      if (response.subscriptionAccess && !response.subscriptionAccess.accessGranted) {
        navigate('/billing-required', { replace: true });
        return;
      }

      const currentClubSlug = getClubSiteSlugFromHostname();
      if (currentClubSlug) {
        try {
          const profile = await clubsBookingApi.getMyClubProfile();
          const ownSlug = profile?.publicSlug?.trim().toLowerCase();
          if (ownSlug && ownSlug !== currentClubSlug) {
            const ownSiteUrl = getClubSiteUrl(ownSlug);
            const ownSiteOrigin = ownSiteUrl ? new URL(ownSiteUrl).origin : '';
            if (ownSiteOrigin && ownSiteOrigin !== window.location.origin) {
              window.location.assign(`${ownSiteOrigin}/dashboard`);
              return;
            }
          }
        } catch {
          // Fallback to local dashboard route when profile lookup fails.
        }
      }

      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      const message = err?.response?.data?.message;
      setError(Array.isArray(message) ? message.join(', ') : message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#f1f2f7] px-4 py-10">
      <section className="mx-auto grid w-full max-w-5xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm md:grid-cols-[1.1fr_1fr]">
        <div className="hidden bg-[#17754D] p-10 text-white md:block">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Session Monkey" className="h-10 w-10 rounded-md object-contain" />
            <p className="text-4xl font-semibold tracking-tight">Session Monkey</p>
          </div>
          <h2 className="mt-10 text-3xl font-semibold leading-tight">Built for after-school clubs.</h2>
          <p className="mt-4 max-w-sm text-base text-white/80">
            Create activities, share booking links, and track attendee payments from one dashboard.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-white/90">
            <li>Real-time capacity tracking</li>
            <li>Public parent booking pages</li>
            <li>Payment status management</li>
          </ul>
          {mode === 'signup' && referralCode && (
            <p
              className="mt-8 rounded-lg border border-white/15 bg-white/10 px-4 py-3 text-sm font-medium text-white/95"
              data-testid="signup-referral-banner"
            >
              {referralLookupLoading
                ? 'Checking your referral...'
                : referralDisplayName
                  ? `Referred by ${referralDisplayName}`
                  : 'Referral link applied'}
            </p>
          )}
        </div>

        <div className="p-6 md:p-10">
          <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
          <p className="mt-2 text-sm text-slate-600">Manage events, parent bookings, and payments in one place.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            {mode === 'signup' && (
              <>
                <label className="block">
                  <span className="text-sm text-slate-700">Your name</span>
                  <input
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-slate-700">Club name</span>
                  <input
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="e.g. Spring Football Club"
                    required
                  />
                </label>
              </>
            )}

            <label className="block">
              <span className="text-sm text-slate-700">Email</span>
              <input
                type="email"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>

            <label className="block">
              <span className="text-sm text-slate-700">Password</span>
              <input
                type="password"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>

            {mode === 'signup' && (
              <label className="block">
                <span className="text-sm text-slate-700">Confirm password</span>
                <input
                  type="password"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </label>
            )}

            {error && <p className="text-sm text-red-600">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-[#1F8F5F] px-4 py-2 text-white hover:bg-[#1F8F5F] disabled:opacity-70"
            >
              {loading ? 'Please wait...' : mode === 'login' ? 'Login' : 'Create account'}
            </button>
          </form>

          <div className="mt-4 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => {
                setMode(mode === 'login' ? 'signup' : 'login');
                setError('');
              }}
              className="text-sm text-slate-700 underline"
            >
              {mode === 'login' ? 'Need an account? Sign up' : 'Already have an account? Login'}
            </button>
            {mode === 'login' && (
              <Link to="/parent/login" className="text-sm text-slate-700 underline">
                Login as parent
              </Link>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default LoginPage;


